import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { RoyaltyReport } from '@/types';
import { useToast } from './use-toast';

export const useRoyaltyReports = (authorId?: string) => {
  const { toast } = useToast();

  return useQuery({
    queryKey: ['royaltyReports', authorId],
    queryFn: async (): Promise<RoyaltyReport[]> => {
      let query = supabase
        .from('royalty_reports')
        .select(`
          *,
          books:book_id (
            title,
            isbn
          ),
          profiles:author_id (
            tax_percentage
          )
        `)
        .order('year', { ascending: false })
        .order('quarter', { ascending: false });
      
      if (authorId) {
        query = query.eq('author_id', authorId);
      }
      
      const { data, error } = await query;
      
      if (error) {
        toast({
          title: 'Error loading royalty reports',
          description: error.message,
          variant: 'destructive',
        });
        throw error;
      }
      
      // Convert database fields to match our front-end model
      return data.map(report => ({
        id: report.id,
        quarter: report.quarter,
        year: report.year,
        authorId: report.author_id,
        bookId: report.book_id,
        salesCount: report.sales_count,
        amount: report.amount,
        currency: report.currency,
        status: report.status,
        createdAt: report.created_at,
        books: report.books,
        profiles: report.profiles,
        // Enhanced fields
        unitsSold: report.units_sold || report.sales_count || 0,
        grossSales: report.gross_sales || report.amount || 0,
        netSales: report.net_sales !== undefined ? report.net_sales : 0, // Use actual net_sales, default to 0
        royaltyAmount: report.royalty_amount || report.amount || 0,
        bookFormat: report.book_format || 'paperback',
        country: report.country || 'US',
      })) as unknown as RoyaltyReport[];
    }
  });
};

export const useRoyaltyReport = (reportId: string) => {
  const { toast } = useToast();

  return useQuery({
    queryKey: ['royaltyReport', reportId],
    queryFn: async (): Promise<RoyaltyReport> => {
      const { data, error } = await supabase
        .from('royalty_reports')
        .select(`
          *,
          books:book_id (
            title,
            isbn,
            author_id
          ),
          profiles:author_id (
            name,
            email,
            tax_percentage
          )
        `)
        .eq('id', reportId)
        .single();
      
      if (error) {
        toast({
          title: 'Error loading royalty report',
          description: error.message,
          variant: 'destructive',
        });
        throw error;
      }
      
      // Convert database fields to match our front-end model
      return {
        id: data.id,
        quarter: data.quarter,
        year: data.year,
        authorId: data.author_id,
        bookId: data.book_id,
        salesCount: data.sales_count,
        amount: data.amount,
        currency: data.currency,
        status: data.status,
        createdAt: data.created_at,
        books: data.books,
        profiles: data.profiles,
        // Enhanced fields
        unitsSold: data.units_sold || data.sales_count || 0,
        grossSales: data.gross_sales || data.amount || 0,
        netSales: data.net_sales !== undefined ? data.net_sales : 0, // Use actual net_sales, default to 0
        royaltyAmount: data.royalty_amount || data.amount || 0,
        bookFormat: data.book_format || 'paperback',
        country: data.country || 'US',
      } as unknown as RoyaltyReport;
    },
    enabled: !!reportId
  });
};

export const useCreateRoyaltyReport = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (reportData: {
      quarter: number;
      year: number;
      authorId: string;
      bookId: string;
      salesCount: number;
      amount: number;
      currency?: string;
      status?: string;
      unitsSold?: number;
      grossSales?: number;
      netSales?: number;
      royaltyAmount?: number;
      bookFormat?: string;
      country?: string;
    }) => {
      // Convert front-end model to database fields
      const dbReport = {
        quarter: reportData.quarter,
        year: reportData.year,
        author_id: reportData.authorId,
        book_id: reportData.bookId,
        sales_count: reportData.salesCount,
        amount: reportData.amount,
        currency: reportData.currency || 'USD',
        status: reportData.status || 'draft',
        units_sold: reportData.unitsSold || reportData.salesCount,
        gross_sales: reportData.grossSales || reportData.amount,
        net_sales: reportData.netSales !== undefined ? reportData.netSales : 0, // Use actual netSales, default to 0
        royalty_amount: reportData.royaltyAmount || reportData.amount,
        book_format: reportData.bookFormat || 'paperback',
        country: reportData.country || 'US',
      };
      
      const { data, error } = await supabase
        .from('royalty_reports')
        .insert(dbReport)
        .select()
        .single();
      
      if (error) {
        toast({
          title: 'Error creating royalty report',
          description: error.message,
          variant: 'destructive',
        });
        throw error;
      }
      
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['royaltyReports'] });
      toast({
        title: 'Success',
        description: 'Royalty report has been created.',
      });
    }
  });
};

export const useUpdateRoyaltyReport = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      reportId,
      reportData
    }: {
      reportId: string;
      reportData: {
        quarter?: number;
        year?: number;
        bookId?: string;
        salesCount?: number;
        amount?: number;
        currency?: string;
        status?: string;
        unitsSold?: number;
        grossSales?: number;
        netSales?: number;
        royaltyAmount?: number;
        bookFormat?: string;
        country?: string;
      };
    }) => {
      // Convert front-end model to database fields
      const dbReport = {
        ...(reportData.quarter !== undefined && { quarter: reportData.quarter }),
        ...(reportData.year !== undefined && { year: reportData.year }),
        ...(reportData.bookId !== undefined && { book_id: reportData.bookId }),
        ...(reportData.salesCount !== undefined && { sales_count: reportData.salesCount }),
        ...(reportData.amount !== undefined && { amount: reportData.amount }),
        ...(reportData.currency !== undefined && { currency: reportData.currency }),
        ...(reportData.status !== undefined && { status: reportData.status }),
        ...(reportData.unitsSold !== undefined && { units_sold: reportData.unitsSold }),
        ...(reportData.grossSales !== undefined && { gross_sales: reportData.grossSales }),
        ...(reportData.netSales !== undefined && { net_sales: reportData.netSales }), // Properly handle netSales updates
        ...(reportData.royaltyAmount !== undefined && { royalty_amount: reportData.royaltyAmount }),
        ...(reportData.bookFormat !== undefined && { book_format: reportData.bookFormat }),
        ...(reportData.country !== undefined && { country: reportData.country })
      };
      
      const { data, error } = await supabase
        .from('royalty_reports')
        .update(dbReport)
        .eq('id', reportId)
        .select()
        .single();
      
      if (error) {
        toast({
          title: 'Error updating royalty report',
          description: error.message,
          variant: 'destructive',
        });
        throw error;
      }
      
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['royaltyReport', variables.reportId] });
      queryClient.invalidateQueries({ queryKey: ['royaltyReports'] });
      toast({
        title: 'Success',
        description: 'Royalty report has been updated.',
      });
    }
  });
};
